import {INutritionalProperties} from '../../models/food-group';
import {firstValueFrom, Observable} from 'rxjs';
import {MealPlanState, MealPlanTypeEnum} from '../../models/meal-plans';
import {PlanPeriodClass} from '../../class/meal-plan/plan-period.class';
import {MxStrategy} from '../../class/macronutrient-distribution-strategy/mx.strategy';
import {NzMarks} from 'ng-zorro-antd/slider';
import {IMacronutrientDistributionValues} from '../../class/macronutrient-distribution-strategy/interfaces/macronutrient-distribution-values.interface';
import {cloneDeep} from 'lodash';
import {OmsStrategy} from '../../class/macronutrient-distribution-strategy/oms.strategy';
import {MacronutrientDistributionTypeEnum} from '../../class/macronutrient-distribution-strategy/enums/macronutrient-distribution-type.enum';
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {MacronutrientDistribution} from '../../class/macronutrient-distribution-strategy/macronutrient-distribution';
import {Select, Store} from '@ngxs/store';
import {SmaeStrategy} from '../../class/macronutrient-distribution-strategy/smae.strategy';
import {DgaUsdaHhsStrategy} from '../../class/macronutrient-distribution-strategy/dga-usda-hhs.strategy';
import {CalSmaeGramsPipe} from '../../pipes/calc-smae-grams/cal-smae-grams.pipe';
import {MacronutrientEnum} from '../../class/macronutrient-distribution-strategy/enums/macronutrient.enum';
import {ISmaeResult} from '../../class/macronutrient-distribution-strategy/enums/smae-result.interface';
import {MeasurementAction, MeasurementState} from '../../models/measurement';
import {ServiceState} from '../../models/service';
import {MacronutrientDistributionNamespace, MealPlanBuilderAction, MealPlanBuilderState} from '../../models/meal-plan-builder';
import {takeWhile} from 'rxjs/operators';
import {MealPlanClass} from '../../class/meal-plan/meal-plan.class';

export interface IStandardDistributionValues {
  name: string;
  values: {
    carbohydrates: string,
    lipids: string,
    protein: string,
    fiber: string
  };
}

@Component({
  selector: 'app-macronutrient-distribution',
  templateUrl: './macronutrient-distribution.component.html',
  styleUrls: ['./macronutrient-distribution.component.scss']
})
export class MacronutrientDistributionComponent implements OnInit, OnChanges, OnDestroy {
  @Select(MealPlanBuilderState.getMacronutrientDistribution)
  macronutrientDistribution$: Observable<Partial<MacronutrientDistributionNamespace.IMacronutrientDistribution> | null>;

  distributionProperties$: Observable<IMacronutrientDistributionValues> =
    this.store.select<IMacronutrientDistributionValues>(MealPlanState.getDistributionProperties);

  distributionStrategy?: MacronutrientDistribution = new OmsStrategy();

  @Input() disabled: boolean;
  @Input() readOnly: boolean;
  @Input() planPeriod: PlanPeriodClass;
  @Input() mealPlan: MealPlanClass;
  @Input() nutritionalPropertiesMealPlan: INutritionalProperties;
  @Input() patientId: string;
  @Input() isHideMessages: boolean;
  @Output() changeDistribution: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() changeDistributionStandardValues: EventEmitter<IStandardDistributionValues> = new EventEmitter<IStandardDistributionValues>();
  @Output() hideMessages: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() changeDistributionStrategy: EventEmitter<{ strategyValue: MacronutrientDistribution, strategyName: MacronutrientDistributionTypeEnum }> =
    new EventEmitter<{ strategyValue: MacronutrientDistribution, strategyName: MacronutrientDistributionTypeEnum }>();

  @Input() set setSmaeGrams(value: number) {
    if (!value) {
      return;
    }

    this.metaKcal = value;

    this.calcGrams();
  }

  getSmaeGrams: Partial<ISmaeResult> | null = null;

  macronutrientDistributionTypeEnum = MacronutrientDistributionTypeEnum;
  currentValues: IMacronutrientDistributionValues;
  blockedMacros: string[] = [];
  marks: NzMarks = {
    10: '10%',
    30: '30%',
    50: '50%',
    70: '70%',
    90: '90%'
  };
  strategyName: MacronutrientDistributionTypeEnum = MacronutrientDistributionTypeEnum.SMAE;
  patientWeight = 0;
  activeObservables: boolean;
  isSmaeDistribution: boolean;
  metaKcal = 0;
  

  constructor(
    private store: Store
  ) {
  }

  ngOnInit(): void {
    this.initValues();
    this.initValuesHttp().then();
    this.listeners();
  }

  private calcGrams() {

    const value = this.metaKcal;
    const calSmaeGramsPipe: CalSmaeGramsPipe = new CalSmaeGramsPipe();

    const smaeGrams = {
      calories: Number(value.toFixed(2)),
        protein: Number(calSmaeGramsPipe.transform(value, this.distributionStrategy, MacronutrientEnum.PROTEIN)),
        lipids: Number(calSmaeGramsPipe.transform(value, this.distributionStrategy, MacronutrientEnum.LIPIDS)),
        carbohydrates: Number(calSmaeGramsPipe.transform(value, this.distributionStrategy, MacronutrientEnum.CARBOHYDRATES)),
        fiber: Number(calSmaeGramsPipe.transform(value, this.distributionStrategy, MacronutrientEnum.FIBER)),
    };
    this.store.dispatch(
      new MealPlanBuilderAction.PatchMacronutrientDistribution({smaeGrams})
    );
  }

  private initValues(): void {
    this.activeObservables = true;
    this.isSmaeDistribution = false;
  }

  private listeners(): void {
    this.distributionProperties$.pipe(takeWhile(() => this.activeObservables)).subscribe(data => {
      if (!data) return;
      this.isSmaeDistribution = (this.mealPlan?.metadata?.mealPlanType === MealPlanTypeEnum.SMAE);
      this.currentValues = cloneDeep(data);
      if (this.isSmaeDistribution) {
        this.strategyName = this.mealPlan?.metadata?.distribution?.current || MacronutrientDistributionTypeEnum.SMAE;
      } else {
        this.strategyName = this.planPeriod.metadata?.distribution?.current || MacronutrientDistributionTypeEnum.SMAE;
      }
      this.changeValuesFromMetadataOrInit();
    });

    this.macronutrientDistribution$.pipe(takeWhile(() => this.activeObservables)).subscribe({
      next: (macronutrientDistribution: Partial<MacronutrientDistributionNamespace.IMacronutrientDistribution> | null): void => {
        if (!macronutrientDistribution) return;
        this.getSmaeGrams = macronutrientDistribution.smaeGrams;
        if (this.mealPlan?.metadata?.mealPlanType === MealPlanTypeEnum.SMAE) {
          this.strategyName = this.mealPlan?.metadata?.distribution?.current;
        } else {
          this.strategyName = this.planPeriod?.metadata?.distribution?.current;
        }
        // if (!macronutrientDistribution[this.strategyName]) return;
        // this.distributionStrategy.setValues(macronutrientDistribution[this.strategyName]);
      }
    });
  }

  formatterPercent = (value: number): string => `${Math.trunc(value)} %`;

  parserPercent = (value: string): string => {
    value = value || '0';
    return value.replace(' %', '');
  }

  async initValuesHttp(): Promise<void> {
    try {
      let measurements = cloneDeep(await firstValueFrom(this.store.selectOnce(MeasurementState.getMeasurementsHistorialHeaders)));
      if (!measurements || measurements.length === 0) {
        const service = cloneDeep(await firstValueFrom(this.store.selectOnce(ServiceState.getActive)));
        if (this.patientId && service) {
          await firstValueFrom(this.store.dispatch(new MeasurementAction.GetPatientMeasurementHeaders(this.patientId, service?._id)));
          measurements = cloneDeep(await firstValueFrom(this.store.selectOnce(MeasurementState.getMeasurementsHistorialHeaders)));
        }
      }
      this.patientWeight = (measurements ?? []).find(measurement => measurement.name === 'weight')?.lastValue || 0;
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Cambia la estrategia para el estandar de distribución
   * @param strategy strategy
   */
  setDistributionStrategy(strategy: MacronutrientDistribution): void {
    this.distributionStrategy = cloneDeep(strategy);
  }

  /**
   * Método que se llama cuando el usuario quiere cambiar de estandar de distribución
   * @param strategy strategy
   */
  changeStrategy(strategy: MacronutrientDistributionTypeEnum): void {
    this.factoryStrategy(strategy);
    this.strategyName = strategy;
    this.changeValuesFromMetadataOrInit();
  }

  factoryStrategy(strategy: MacronutrientDistributionTypeEnum): void {
    switch (strategy) {
      case MacronutrientDistributionTypeEnum.OMS:
        this.setDistributionStrategy(new OmsStrategy());
        break;
      case MacronutrientDistributionTypeEnum.MX:
        this.setDistributionStrategy(new MxStrategy());
        break;
      case MacronutrientDistributionTypeEnum.SMAE:
        this.setDistributionStrategy(new SmaeStrategy());
        break;
      case MacronutrientDistributionTypeEnum.DGA_USDA_HHS:
        this.setDistributionStrategy(new DgaUsdaHhsStrategy());
    }
  }

  /**
   * Cuando un bloque (macro nutriente) se bloquea para no ser parte del calculo
   * de porcentajes
   */
  onChangeBlock(): void {
    try {
      // comprobar que solo se puedan tener bloqueados n - 2 elementos
      const keys = Object.keys(this.distributionStrategy);
      this.blockedMacros = keys.reduce((last, current) => {
        if (this.distributionStrategy[current].block) {
          return [...last, current];
        } else {
          return last;
        }
      }, []);
      if (!this.planPeriod) return;
      // cuando se bloquea o desbloquea un campo se emite el cambio para guardarlo en metadata.
      this.setValuesToMetadata().then();
    } catch (e) {
    }
  }

  async setValuesToMetadata(): Promise<void> {
    if (this.isSmaeDistribution) {
      this.mealPlan.setMacronutrientDistribution(this.distributionStrategy, this.strategyName);
    } else {
      this.planPeriod.setMacronutrientDistribution(this.distributionStrategy, this.strategyName);
    }

    await firstValueFrom(
      this.store.dispatch(new MealPlanBuilderAction.PatchMacronutrientDistribution({
        [this.strategyName as MacronutrientDistributionTypeEnum]: this.distributionStrategy,
      }))
    );
  }

  /**
   * Método para calcular los porcentajes mediante iteración de índices
   * Método 2
   * @param index index
   */
  onChangePercentageIndex(index: number): void {
    const macros = Object.keys(this.distributionStrategy);
    const total = Object.keys(this.distributionStrategy).reduce((last, current) => {
      return last + this.distributionStrategy[current].defaultValue;
    }, 0);
    this.decrease(total, macros[index + 1] ? index + 1 : 0);
    this.distributionStrategy.verify(this.currentValues);
    this.changeDistribution.emit(this.distributionStrategy.getAlertMessages());
    this.changeDistributionStandardValues.emit(this.getStandardValues());
    
    this.planPeriod.setMacronutrientDistribution(this.distributionStrategy, this.strategyName);
    this.mealPlan.setMacronutrientDistribution(this.distributionStrategy, this.strategyName);
  }

  /**
   * Método que determina el valor a restar/sumar a los diferentes macros de distribución con el método 2
   * @param total total
   * @param index index
   * @param iteration iteration
   */
  decrease(total: number, index: number, iteration = 0): void {
    const macros = Object.keys(this.distributionStrategy);
    const currentValue = this.distributionStrategy[macros[index]].defaultValue;
    const value = 100 - total;
    const surplus = currentValue + value;
    if (surplus < 0) {
      this.distributionStrategy[macros[index]].setDefaultValue(0);
      this.decrease(100 + (surplus * -1), macros[index + 1] ? index + 1 : 0, iteration);
    } else {
      this.distributionStrategy[macros[index]].setDefaultValue(surplus);
    }
  }


  /**
   * Método para calcular los porcentajes mediante el bloque de bloques (macronutrientes)
   * Método 1
   * @param macro macro
   */
  async onChangePercentage(macro: string): Promise<void> {

    let totalOfBlocked = 0;
    for (const key of this.blockedMacros) {
      totalOfBlocked += this.distributionStrategy[key].defaultValue;
    }
    const total = Object.keys(this.distributionStrategy).reduce((last, current) => {
      return last + this.distributionStrategy[current].defaultValue;
    }, 0);
    const excedent = total - totalOfBlocked;
    if (excedent + totalOfBlocked > 100 && this.blockedMacros.length > 0) {
      setTimeout(() => {
        this.distributionStrategy[macro].setDefaultValue(0);
        const totalRest = Object.keys(this.distributionStrategy).reduce((last, current) => {
          return last + this.distributionStrategy[current].defaultValue;
        }, 0);
        this.distributionStrategy[macro].setDefaultValue(100 - totalRest);
        this.increase(total, macro, true);
        this.distributionStrategy.verify(this.currentValues);
        this.changeDistribution.emit(this.distributionStrategy.getAlertMessages());
        this.changeDistributionStandardValues.emit(this.getStandardValues());
        // cuando cambia el valor de un macro, se guarda el nuevo valor en metadata.
        this.setValuesToMetadata().then();
        this.calcGrams();
      });
      return;
    }
    this.increase(total, macro);
    this.distributionStrategy.verify(this.currentValues);
    this.changeDistribution.emit(this.distributionStrategy.getAlertMessages());
    this.changeDistributionStandardValues.emit(this.getStandardValues());
    // cuando cambia el valor de un macro, se guarda el nuevo valor en metadata.
    this.setValuesToMetadata().then();
    this.calcGrams();
  }

  /**
   * Método que determina el valor a restar/sumar a los diferentes macros de distribución con el método 1
   * @param total total
   * @param property property
   * @param addSelf self
   */
  increase(total: number, property: string, addSelf: boolean = false): void {

    const probability = {};
    let hnum = -2;
    let hprop = '';
    Object.keys(this.distributionStrategy).forEach((prop) => {
      if (hprop === '') {
        hprop = prop;
      }
      if (property === prop) {
        probability[prop] = -100;
      } else {
        probability[prop] = this.distributionStrategy[prop].block
          ? -1
          : Math.random() * this.distributionStrategy[prop].defaultValue;
        if (this.distributionStrategy[prop].last) {
          probability[prop] = probability[prop] - 10; // Decrease probability
        }
      }
      if (probability[prop] > hnum) {
        hnum = probability[prop];
        hprop = prop;
      }
    });
    const newValue = this.distributionStrategy[hprop].defaultValue + (100 - total);
    if (newValue < 0) {
      this.distributionStrategy[hprop].defaultValue = 0;
      this.onChangePercentage(property).then();
    } else {
      this.distributionStrategy[hprop].defaultValue += (100 - total);
      if (addSelf) {
        this.distributionStrategy[property].defaultValue += (100 - total) * -1;
      }
    }
  }

  /**
   * Detecta cuando la propiedad de currentValue con los macros cambia
   * @param changes changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.planPeriod) return;
  }

  changeValuesFromMetadataOrInit(): void {
    try {
      if (this.isSmaeDistribution) {
        if (this.mealPlan?.metadata?.distribution && this.mealPlan?.metadata?.distribution[this.strategyName]) {
          this.factoryStrategy(this.strategyName);
          this.distributionStrategy.setValues(this.mealPlan.metadata.distribution[this.strategyName]);
        } else {
          this.initializeStrategyWithCurrentValues();
          this.mealPlan.setMacronutrientDistribution(this.distributionStrategy, this.strategyName);
        }
      } else {
        if (this.planPeriod?.metadata?.distribution && this.planPeriod.metadata.distribution[this.strategyName]) {
          // se detecta que en metadata se tiene la estrategia, entonces se toman sus valores para asignarlos.
          this.distributionStrategy.setValues(this.planPeriod.metadata.distribution[this.strategyName]);
        } else {
          // se detecta que en metadata no existe la estrategia, entonces se debe inicializar con valores de referencia.
          this.initializeStrategyWithCurrentValues();
          this.planPeriod.setMacronutrientDistribution(this.distributionStrategy, this.strategyName);
        }
      }

      // se determina si existen campos bloqueados (importante para las validaciones de los sliders)
      this.onChangeBlock();
      // se verifican los valores de macronutrientes.
      this.distributionStrategy.verify(this.currentValues);
      this.changeDistribution.emit(this.distributionStrategy.getAlertMessages());
      this.changeDistributionStandardValues.emit(this.getStandardValues());
      this.changeDistributionStrategy.emit({strategyName: this.strategyName, strategyValue: this.distributionStrategy});
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Lanza el método init de la estrategia pasándole los valores de los macros
   */
  initializeStrategyWithCurrentValues(): void {
    // cuando se cambia de tab para un nuevo periodo, se debe instance de nuevo en la estrategia que esté seleccionada.
    this.factoryStrategy(this.strategyName);
    // tomando los valores que por lo general serán todos en cero, se inicializa la estrategia tomando valores de referencia.
    this.distributionStrategy.init(this.currentValues);
  }

  onToggleViewMessages(value): void {
    this.hideMessages.emit(value);
  }

  getStandardValues(): IStandardDistributionValues {
    return {
      name: this.strategyName,
      values: {
        carbohydrates: this.distributionStrategy.carbohydrates.defaultValue + '%',
        lipids: this.distributionStrategy.lipids.defaultValue + '%',
        protein: this.distributionStrategy.protein.defaultValue + '%',
        fiber: this.distributionStrategy.fiber.defaultValue + '%'
      }
    };
  }

  ngOnDestroy(): void {
    this.activeObservables = false;
  }
}
